import { useState, type ReactNode } from 'react';

import { api, qs } from '../api';
import { Aviso, Cartao, Estado, Painel, pct, useAcao, useCarga } from '../comum';
import { baixarJson } from './Configuracoes';
import { ROTULO_VERSAO } from './inventario/tipos';
import type { RelatorioExecutivo } from '../tipos';

/** Últimos 12 meses fechados, o mais recente primeiro. */
function mesesRecentes(): readonly { valor: string; rotulo: string }[] {
  const hoje = new Date();
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - i - 1, 1);
    return {
      valor: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      rotulo: d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
    };
  });
}

export function Relatorios({
  aoPerderSessao,
  podeExportar,
}: {
  aoPerderSessao: () => void;
  podeExportar: boolean;
}): ReactNode {
  const meses = mesesRecentes();
  const [mes, setMes] = useState(meses[0]?.valor ?? '');
  const [setor, setSetor] = useState('');
  const [pacote, setPacote] = useState<Record<string, unknown> | null>(null);
  const [verResumo, setVerResumo] = useState(false);

  /**
   * O resumo em prosa.
   *
   * Carregado sob demanda: chama o modelo, que roda em CPU dentro do cliente.
   * A tela mostra o mesmo texto que vai ser entregue na segunda — ver antes de
   * sair para o chefe de alguém é o mínimo devido a quem assina embaixo.
   */
  const resumo = useCarga<{
    readonly titulo: string;
    readonly corpo: string;
    readonly entregaEm: string;
  }>(
    () =>
      verResumo
        ? api.get('/api/relatorios/resumo/previa')
        : Promise.resolve({ titulo: '', corpo: '', entregaEm: '' }),
    [verResumo],
    aoPerderSessao,
  );
  const acao = useAcao(aoPerderSessao);

  const carga = useCarga<RelatorioExecutivo>(
    () => api.get(`/api/relatorios/executivo${qs({ mes })}`),
    [mes],
    aoPerderSessao,
  );

  const gerarBenchmark = (): void => {
    void acao.rodar(async () => {
      setPacote(
        await api.get<Record<string, unknown>>(`/api/relatorios/benchmark${qs({ mes, setor })}`),
      );
      return 'Pacote gerado. Confira o conteúdo abaixo antes de baixar.';
    });
  };

  const r = carga.dados;

  return (
    <>
      {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}

      {/*
        O resumo escrito.
        O relatório de números existe e é bom — e exige que alguém o traduza
        num parágrafo toda segunda. Aqui a IA faz esse trabalho, e o texto é o
        mesmo que será entregue.
      */}
      <Painel
        titulo="Resumo para a diretoria"
        sub="Escrito pela IA a partir dos números abaixo. Nenhuma contagem sai do texto sem estar no relatório."
      >
        {!verResumo ? (
          <button className="primario encolhe" onClick={() => setVerResumo(true)}>
            ✦ Gerar o resumo em texto
          </button>
        ) : (
          <Estado carga={resumo}>
            {resumo.dados !== null && resumo.dados.corpo !== '' && (
              <>
                <h3 style={{ marginTop: 0 }}>{resumo.dados.titulo}</h3>
                <div className="resumo-prosa">
                  {resumo.dados.corpo.split('\n').map((linha, i) => (
                    <p key={i}>{linha}</p>
                  ))}
                </div>
                <p className="suave" style={{ fontSize: 12.5 }}>
                  Entrega: {resumo.dados.entregaEm}
                </p>
                <button onClick={() => setVerResumo(false)}>Fechar</button>
              </>
            )}
          </Estado>
        )}
      </Painel>

      <div className="linha">
        <div className="encolhe" style={{ minWidth: 220 }}>
          <select value={mes} onChange={(e) => setMes(e.target.value)}>
            {meses.map((m) => (
              <option key={m.valor} value={m.valor}>
                {m.rotulo}
              </option>
            ))}
          </select>
        </div>
        <span className="cresce" />
        <button
          className="encolhe"
          disabled={r === null}
          onClick={() => r !== null && baixarJson(r, `relatorio-${mes}.json`)}
        >
          Baixar relatório
        </button>
      </div>

      <Estado carga={carga}>
        {r !== null && (
          <>
            <Aviso>{r.observacao}</Aviso>

            <Painel titulo="Parque">
              <div className="cartoes">
                <Cartao rotulo="Dispositivos" valor={r.parque.total} />
                {/*
                  🔴 Sem `slice`, e com rótulo traduzido.

                  Cortava em 3. A lista tinha 4, e a quarta era
                  `win7_ou_anterior: 1` — a única máquina do parque em Windows 7,
                  escondida justamente no relatório que existe para mostrá-la.

                  As versões são poucas por natureza (win11, win10, win7 ou
                  anterior, n/a): não há lista longa a temer aqui.
                */}
                {r.parque.porVersaoWindows.map((v) => (
                  <Cartao
                    key={v.versao}
                    rotulo={ROTULO_VERSAO[v.versao] ?? v.versao}
                    valor={v.total}
                    tom={v.versao === 'win7_ou_anterior' ? 'erro' : 'neutro'}
                  />
                ))}
                <Cartao
                  rotulo="Sem inventário há 30d"
                  valor={r.parque.semInventarioRecente}
                  destaque
                />
                <Cartao rotulo="Sem agente" valor={r.parque.semAgente} destaque />
              </div>
              {/*
                🔴 A quebra por versão só conta Windows.

                Num parque com Linux e macOS, somar os cartões dá menos que o
                total — e o painel se chama "Parque". Quem confere a conta
                encontra máquinas desaparecidas e não tem como saber se é bug,
                se sumiram do inventário, ou se é assim mesmo.
              */}
              <ForaDoWindows
                total={r.parque.total}
                emWindows={r.parque.porVersaoWindows.reduce((s, v) => s + v.total, 0)}
              />
            </Painel>

            <Painel titulo="Service desk">
              <div className="cartoes">
                <Cartao rotulo="Abertos no período" valor={r.serviceDesk.abertosNoPeriodo} />
                <Cartao rotulo="Fechados" valor={r.serviceDesk.fechadosNoPeriodo} />
                <Cartao rotulo="Abertos agora" valor={r.serviceDesk.abertosNoFim} />
                <Cartao
                  rotulo="Mediana p/ fechar"
                  valor={
                    r.serviceDesk.medianaHorasParaFechar === null
                      ? '—'
                      : `${r.serviceDesk.medianaHorasParaFechar}h`
                  }
                />
                <Cartao rotulo="Sem responsável" valor={r.serviceDesk.semResponsavel} destaque />
              </div>
              <p className="suave">
                {r.serviceDesk.fechadosComResolucao} de {r.serviceDesk.fechadosNoPeriodo} fechados
                têm resolução escrita — é essa parcela que a IA consegue citar.
              </p>
            </Painel>

            <Painel titulo="Conformidade">
              <div className="cartoes">
                <Cartao rotulo="Regras ativas" valor={r.conformidade.regrasAtivas} />
                <Cartao rotulo="Violações abertas" valor={r.conformidade.violacoesAbertas} />
                <Cartao rotulo="Detectadas no período" valor={r.conformidade.detectadasNoPeriodo} />
                <Cartao rotulo="Resolvidas" valor={r.conformidade.resolvidasNoPeriodo} />
              </div>
            </Painel>

            <Painel titulo="Remediação">
              <div className="cartoes">
                <Cartao rotulo="Criadas" valor={r.remediacao.criadas} />
                <Cartao rotulo="Concluídas" valor={r.remediacao.concluidas} />
                <Cartao rotulo="Interrompidas" valor={r.remediacao.interrompidas} destaque />
                <Cartao rotulo="Máquinas corrigidas" valor={r.remediacao.maquinasComSucesso} />
              </div>
            </Painel>

            <Painel titulo="Inteligência artificial">
              <div className="cartoes">
                <Cartao rotulo="Sugestões" valor={r.ia.sugestoesGeradas} />
                <Cartao rotulo="Aceitas" valor={r.ia.aceitas} />
                <Cartao rotulo="Editadas" valor={r.ia.editadas} />
                <Cartao rotulo="Aproveitamento" valor={pct(r.ia.taxaDeAproveitamento)} />
              </div>
            </Painel>
          </>
        )}
      </Estado>

      {podeExportar && (
        <Painel
          titulo="Benchmark entre clientes"
          sub="Compara o seu ambiente com a mediana de outros clientes atendidos pela Lenanzo. Nada é enviado pela rede: você vê o conteúdo, baixa o arquivo e entrega ao técnico na visita."
        >
          <div className="linha">
            <div>
              <input
                placeholder="Setor (opcional — ex.: Varejo, Indústria)"
                value={setor}
                onChange={(e) => setSetor(e.target.value)}
              />
            </div>
            <button className="encolhe" disabled={acao.executando} onClick={gerarBenchmark}>
              Gerar pacote
            </button>
          </div>

          {pacote !== null && (
            <>
              <Aviso tipo="ok">{String(pacote['declaracao'] ?? '')}</Aviso>
              <p className="suave">
                Este é o conteúdo <strong>inteiro</strong> do arquivo. Confira antes de autorizar.
              </p>
              <pre className="mono">{JSON.stringify(pacote, null, 2)}</pre>
              <button
                className="primario"
                onClick={() => baixarJson(pacote, `benchmark-${mes}.json`)}
              >
                Autorizar e baixar
              </button>
            </>
          )}
        </Painel>
      )}
    </>
  );
}

/**
 * As máquinas que a quebra por versão de Windows não alcança.
 *
 * 🔴 Existe porque a soma não fechava e a tela não dizia nada. Linux e macOS
 * não têm "versão do Windows" — e isso é uma informação, não uma ausência.
 * Silenciar produz o pior dos dois mundos: o leitor confere, não bate, e
 * conclui que o produto perdeu máquinas.
 */
function ForaDoWindows({ total, emWindows }: { total: number; emWindows: number }): ReactNode {
  const fora = total - emWindows;
  if (fora <= 0) return null;

  return (
    <p className="corte" role="status">
      Os cartões acima somam <strong>{emWindows}</strong> de <strong>{total}</strong>. As outras{' '}
      <strong>{fora}</strong> não rodam Windows — a quebra por versão não se aplica a elas.
    </p>
  );
}
